import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import useSiteSettings from '@/lib/useSiteSettings';
import PageHero from '@/components/PageHero';
import { getJournal } from '@/lib/api';
import PictureImg from '@/components/PictureImg';

const fmt = (s) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

export default function Journal() {
    useDocumentMeta({
        title: 'Journal · Maison Touré',
        description: 'Field notes, studio visits and writings from Maison Touré on the practice of contemporary African art and slow collecting.',
    });
    const reveal = useReveal();
    const settings = useSiteSettings();
    const hero = settings?.journal_page || {};
    const [items, setItems] = useState([]);
    useEffect(() => { getJournal().then(setItems); }, []);

    const featured = items[0];
    const rest = items.slice(1);

    return (
        <div ref={reveal} data-testid="journal-page" style={{ paddingTop: 144 }}>
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-12">
                <PageHero
                    testid="journal-hero"
                    overline={hero.hero_overline ?? 'The Journal'}
                    title={hero.hero_title ?? 'Field notes.'}
                    intro={hero.hero_intro ?? 'Studio visits, editorial essays, and writings on the practice of slow collecting — by Fatym Touré and invited contributors.'}
                />

                {/* FEATURED ENTRY */}
                {featured && (
                    <Link
                        to={`/journal/${featured.slug}`}
                        className="reveal group block mt-32 md:mt-40 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center"
                        data-testid={`journal-featured-${featured.slug}`}
                    >
                        <div className="md:col-span-7">
                            <div className="img-wrap aspect-[5/4]" style={{ background: 'var(--bg-3)' }}>
                                <PictureImg src={featured.cover_image} alt={featured.title} loading="lazy" />
                            </div>
                        </div>
                        <div className="md:col-span-4 md:col-start-9">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>{featured.category}</span>
                            <h2
                                className="mt-6"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontStyle: 'italic',
                                    fontWeight: 500,
                                    fontSize: 'clamp(36px, 4.4vw, 64px)',
                                    lineHeight: 1.06,
                                    letterSpacing: '-0.018em',
                                }}
                            >
                                {featured.title}
                            </h2>
                            <p
                                className="mt-6"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontWeight: 400,
                                    fontSize: 17,
                                    lineHeight: 1.7,
                                    color: 'var(--ink-2)',
                                }}
                            >
                                {featured.excerpt}
                            </p>
                            <div
                                className="mt-8"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontStyle: 'italic',
                                    fontSize: 14,
                                    color: 'var(--ink-3)',
                                }}
                            >
                                By {featured.author} &nbsp;·&nbsp; {fmt(featured.published_at)}
                            </div>
                            <span className="btn-ghost mt-10 inline-flex">Read the essay</span>
                        </div>
                    </Link>
                )}

                {/* REST OF ENTRIES */}
                <div className="mt-32 md:mt-48 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-32 pb-40 md:pb-56">
                    {rest.map((j, i) => (
                        <Link
                            to={`/journal/${j.slug}`}
                            key={j.slug}
                            className={`reveal group block ${i % 2 === 1 ? 'md:mt-32' : ''}`}
                            data-testid={`journal-item-${j.slug}`}
                        >
                            <div className="img-wrap aspect-[4/3]" style={{ background: 'var(--bg-3)' }}>
                                <PictureImg src={j.cover_image} alt={j.title} loading="lazy" />
                            </div>
                            <div className="mt-8">
                                <div className="flex items-baseline justify-between gap-6">
                                    <span className="overline" style={{ color: 'var(--ink-2)' }}>{j.category}</span>
                                    <span
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontSize: 14,
                                            color: 'var(--ink-3)',
                                        }}
                                    >
                                        {fmt(j.published_at)}
                                    </span>
                                </div>
                                <h2
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontWeight: 500,
                                        fontSize: 'clamp(28px, 3vw, 44px)',
                                        lineHeight: 1.12,
                                        letterSpacing: '-0.015em',
                                    }}
                                >
                                    {j.title}
                                </h2>
                                <p
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 17,
                                        lineHeight: 1.7,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {j.excerpt}
                                </p>
                                <div
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontSize: 14,
                                        color: 'var(--ink-3)',
                                    }}
                                >
                                    By {j.author}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
